import { Formik } from "formik";
import { useEffect, useState } from "react";
import Spin from "../../../../components/Spin";
import { Label } from "../../../../components/ui/label";
import CustomLabelText from "../../../../components/CustomLabelText";
import { Input } from "../../../../components/ui/input";
import {
  AnswerTypeOptions,
  API,
  fileUpload,
  get,
  QuestionRiskOptions,
} from "../../../../config";
import SearchSelect from "../../../../components/SearchSelect";
import { Button } from "../../../../components/ui/button";
import { AddAdminQuestionSchema } from "../../../../lib/Validation";
import { sectionTypeOptions } from "../../../../config";
import SectionSelect from "../../../../components/SectionSelect";
import { Loader2 } from "lucide-react";
import { useAllOrganizationTypes } from "../../../Auth/SignUpHelper";
import { Switch } from "../../../../components/ui/switch";
import ColorPicker from "../../../../components/ColorPicker";

const AddEditQuestion = ({
  id,
  toggleShowAddEditQuestion,
  edit,
  fetchQuestionData,
}) => {
  const [questionData, setQuestionData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sectionLoading, setSectionLoading] = useState(false);
  const [sectionListing, setSectionListing] = useState([]);
  const [sectionList, setSectionList] = useState([]);

  const [versionLoading, setVersionLoading] = useState(false);
  const [versionListing, setVersionListing] = useState([]);
  const [versionList, setVersionList] = useState([]);

  const { allOrganizationTypeLoading, allOrganizationType = [] } =
    useAllOrganizationTypes({ amlCTFType: true });
  const organizationType = allOrganizationType?.map((item) => {
    const { id: value, name, short_name, ...rest } = item;
    return { label: name, value, ...rest };
  });

  useEffect(() => {
    if (sectionList) {
      let sectionTempListing = sectionList?.map((item) => ({
        ...item,
        value: item?.id,
        label: item?.title,
      }));
      setSectionListing(sectionTempListing || []);
    } else {
      setSectionListing([]);
    }
  }, [sectionList]);

  async function fetchData() {
    try {
      setLoading(true);
      const reponse = await get(`${API.ADMIN_QUESTION_DETAILS}/` + id);
      setQuestionData(reponse?.data?.data);
      setLoading(false);
    } catch (e) {
      setQuestionData(null);
    }
  }

  useEffect(() => {
    if (edit) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleFormSubmit = async (value) => {
    const formData = new FormData();

    formData.append(
      "organization_type_id",
      value?.organization_type_id ? value?.organization_type_id : "",
    );
    formData.append("version_id", value?.version_id ? value?.version_id : "");

    formData.append("order", value?.order ? value?.order : "");
    formData.append("label", value?.label ? value?.label : "");
    formData.append("question", value?.question ? value?.question : "");
    formData.append(
      "section_type",
      value?.section_type ? value?.section_type : "",
    );

    formData.append("yes_color", value.yes_color ? value.yes_color : "#ff0000");
    formData.append("no_color", value.no_color ? value.no_color : "#007718");
    formData.append("risk", value.risk ? value.risk : "");
    formData.append("anwser_type", value.anwser_type ? value.anwser_type : "");

    formData.append("status", value?.status ? 1 : 0);

    formData.append("section_id", value?.section_id ? value?.section_id : "");

    if (edit) {
      formData.append("_method", "put");
    }
    try {
      setLoading(true);
      const { data } = await fileUpload(
        `${API.ADMIN_QUESTION_ADD}${edit ? `/${id}` : ""}`,
        formData,
      );
      setLoading(false);
      if (data) {
        toggleShowAddEditQuestion();
        fetchQuestionData();
      }
      return data?.data;
    } catch (error) {
      setLoading(false);
    }
  };

  const AdminVersionList = async () => {
    try {
      setVersionLoading(true);

      const { data } = await get(`${API.GET_ADMIN_VERSIONS}?limit=-1&page=1`);

      setVersionLoading(false);

      setVersionList(() => [...data.data.data]);
    } catch (e) {
      setVersionList([]);
      setVersionLoading(false);
      return false;
    }
  };

  useEffect(() => {
    AdminVersionList();
  }, []);

  useEffect(() => {
    if (versionList) {
      let versionTempListing = versionList?.map((item) => ({
        ...item,
        value: item?.id,
        label: `${item?.version_number} - ${item?.name}`,
      }));
      setVersionListing(versionTempListing || []);
    } else {
      setVersionListing([]);
    }
  }, [versionList]);

  return (
    <Formik
      initialValues={{
        organization_type_id: questionData?.organization_type_id,
        version_id: questionData?.version_id,
        section_type: questionData?.section_type,
        order: questionData?.order,
        label: questionData?.label,
        question: questionData?.question,
        section_id: questionData?.section_id,
        yes_color: questionData?.yes_color
          ? questionData?.yes_color
          : "#ff0000",
        no_color: questionData?.no_color ? questionData?.no_color : "#007718",
        answer_type: questionData?.answer_type ? questionData?.answer_type : "yes_no",
        risk: questionData?.risk ? questionData?.risk : "",
        status: questionData?.status ? true : false,
      }}
      enableReinitialize={true}
      onSubmit={handleFormSubmit}
      validateOnBlur={true}
      validateOnChange={true}
      validationSchema={AddAdminQuestionSchema}
    >
      {({
        values,
        errors,
        handleChange,
        handleBlur,
        handleSubmit,
        setFieldValue,
        isValid,
      }) => (
        <>
          {loading && <Spin />}
          <form
            onSubmit={handleSubmit}
            noValidate
            className="entry-question md:m-0 m-5"
          >
            <div className="form-box overflow-y-auto overflow-x-hidden max-h-[80vh] p-4 border rounded-lg">
              <div className="grid gap-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="type">
                      <CustomLabelText
                        required={true}
                        labelText="Industry Type"
                        errorField={errors?.organization_type_id}
                        bottomAlign={true}
                      />
                    </Label>
                    <div className="relative">
                      <SearchSelect
                        frameworks={organizationType}
                        optionType={"Industry Type"}
                        handleSelect={(e) => {
                          setFieldValue("organization_type_id", e);
                        }}
                        handleBlur={handleBlur}
                        className={
                          errors?.organization_type_id &&
                          `focus-visible:ring-red-500 border-red-500`
                        }
                        initialValue={values?.organization_type_id}
                        disabled={false}
                      />
                      {allOrganizationTypeLoading && (
                        <span className="absolute top-2 right-2 animate-spin">
                          <Loader2 width={20} height={20} />
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="type">
                      <CustomLabelText
                        required={true}
                        labelText="Version Number"
                        errorField={errors?.version_id}
                        bottomAlign={true}
                      />
                    </Label>
                    <div className="relative">
                      <SearchSelect
                        frameworks={versionListing}
                        optionType={"Version Number"}
                        handleSelect={(e) => {
                          setFieldValue("version_id", e);
                        }}
                        handleBlur={handleBlur}
                        className={
                          errors?.version_id &&
                          `focus-visible:ring-red-500 border-red-500`
                        }
                        initialValue={values?.version_id}
                        disabled={false}
                      />
                      {versionLoading && (
                        <span className="absolute top-2 right-2 animate-spin">
                          <Loader2 width={20} height={20} />
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="section_type">
                      <CustomLabelText
                        required={true}
                        labelText="Section Type"
                        errorField={errors?.section_type}
                        bottomAlign={true}
                      />
                    </Label>

                    <div className="relative">
                      <SearchSelect
                        frameworks={sectionTypeOptions}
                        optionType={"Section Type"}
                        handleSelect={(e) => {
                          setFieldValue("section_type", e);
                          setFieldValue("section_id", "");
                        }}
                        handleBlur={handleBlur}
                        className={
                          errors?.section_type &&
                          `focus-visible:ring-red-500 border-red-500`
                        }
                        disabled={false}
                        initialValue={values?.section_type}
                      />
                    </div>
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="vendor_id">
                      <CustomLabelText
                        labelText="Section"
                        errorField={errors?.section_id}
                        required={true}
                        bottomAlign={true}
                      />
                    </Label>
                    <div className="relative">
                      <SectionSelect
                        sectionType={values?.section_type}
                        organizationType={values?.organization_type_id}
                        frameworkData={sectionListing} // List of sections
                        optionType="Section"
                        handleSelect={(e) => {
                          setFieldValue("section_id", e);
                        }}
                        name="section_id"
                        disabled={!values?.section_type}
                        initialValue={values?.section_id}
                        setSectionList={setSectionList} // Update section list as new data loads
                        setSectionLoading={setSectionLoading} // Control loading state
                        onClear={() => {
                          setFieldValue("section_id", "");
                        }}
                        loading={sectionLoading}
                        className={
                          errors?.section_id &&
                          `focus-visible:ring-red-500 border-red-500`
                        }
                      />
                      {sectionLoading && (
                        <span className="absolute top-2 right-2 animate-spin">
                          <Loader2 width={20} height={20} />
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="order">
                      <CustomLabelText
                        required={true}
                        labelText="Order"
                        errorField={errors?.order}
                      />
                    </Label>
                    <Input
                      id="order"
                      type="number"
                      name="order"
                      min={1} // 🚫 no 0 or negatives
                      step={1}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={values?.order || ""}
                      onKeyDown={(e) => {
                        // block dot, minus, exponent
                        if ([".", "-", "e", "E"].includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      onChange={(e) => {
                        const value = e.target.value;

                        // allow empty while typing
                        if (value === "") {
                          handleChange(e);
                          return;
                        }

                        // only whole numbers greater than 0
                        if (/^[1-9]\d*$/.test(value)) {
                          handleChange(e);
                        }
                      }}
                      onBlur={handleBlur}
                      autoComplete="off"
                      onKeyPress={(e) => {
                        e.which === 13 && e.preventDefault();
                      }}
                      className={
                        errors?.order &&
                        `focus-visible:ring-red-500 border-red-500`
                      }
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="label">
                      <CustomLabelText
                        required={true}
                        labelText="Label"
                        errorField={errors?.label}
                      />
                    </Label>
                    <Input
                      id="label"
                      type="text"
                      name="label"
                      value={values?.label ? values?.label : ""}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      autoComplete="off"
                      onKeyPress={(e) => {
                        e.which === 13 && e.preventDefault();
                      }}
                      className={
                        errors?.label &&
                        `focus-visible:ring-red-500 border-red-500`
                      }
                    />
                  </div>

                  <div className="grid gap-2 col-span-2">
                    <Label htmlFor="question">
                      <CustomLabelText
                        required={false}
                        labelText="Question"
                        errorField={errors?.question}
                      />
                    </Label>
                    <Input
                      id="question"
                      type="text"
                      name="question"
                      value={values?.question ? values?.question : ""}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      autoComplete="off"
                      onKeyPress={(e) => {
                        e.which === 13 && e.preventDefault();
                      }}
                      className={
                        errors?.question &&
                        `focus-visible:ring-red-500 border-red-500`
                      }
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="answer_type">
                      <CustomLabelText
                        labelText="Answer Type"
                        errorField={errors?.answer_type}
                        required={true}
                        bottomAlign={true}
                      />
                    </Label>
                    <SearchSelect
                      frameworks={AnswerTypeOptions}
                      optionType={"Answer Type"}
                      handleSelect={(e) => {
                        setFieldValue("answer_type", e);
                      }}
                      handleBlur={handleBlur}
                      disabled={false}
                      initialValue={values?.answer_type}
                      className={
                        errors?.answer_type &&
                        `focus-visible:ring-red-500 border-red-500`
                      }
                    />
                  </div>

                  {values?.answer_type !== "note" && <div className="grid grid-cols-2">
                    <div className="grid gap-2">
                      <Label htmlFor="yes_color">
                        <CustomLabelText
                          required={true}
                          labelText="Yes Color"
                          errorField={errors?.yes_color}
                          bottomAlign={true}
                        />
                      </Label>
                      <ColorPicker
                        value={values?.yes_color}
                        onChange={(e) => setFieldValue("yes_color", e)}
                      />
                    </div>

                    {/* Default Checkbox */}

                    <div className="grid gap-2">
                      <Label htmlFor="no_color">
                        <CustomLabelText
                          required={true}
                          labelText="No Color"
                          errorField={errors?.no_color}
                          bottomAlign={true}
                        />
                      </Label>
                      <ColorPicker
                        value={values?.no_color}
                        onChange={(e) => setFieldValue("no_color", e)}
                      />
                    </div>
                  </div>}

                  <div className="grid gap-2">
                    <Label htmlFor="risk">
                      <CustomLabelText
                        required={false}
                        labelText="Risk Level"
                        errorField={errors?.risk}
                        bottomAlign={true}
                      />
                    </Label>
                    <SearchSelect
                      frameworks={QuestionRiskOptions}
                      optionType={"Risk Level"}
                      handleSelect={(e) => {
                        setFieldValue("risk", e);
                      }}
                      clearable
                      handleBlur={handleBlur}
                      disabled={false}
                      initialValue={values?.risk}
                      className={
                        errors?.risk &&
                        `focus-visible:ring-red-500 border-red-500`
                      }
                    />
                  </div>

                  <div className="grid gap-2">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium">Active</span>
                      <Switch
                        checked={values?.status}
                        onCheckedChange={(e) => setFieldValue("status", e)}
                        className="data-[state=checked]:bg-webPrimary"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer pt-4 border-t mt-4 flex items-center justify-end">
              <Button type="submit" disabled={!isValid}>
                Save
              </Button>
              <Button
                className="reset ml-3"
                data-dismiss="modal"
                aria-label="Close"
                onClick={(e) => {
                  e.preventDefault();
                  toggleShowAddEditQuestion();
                }}
                variant="secondary"
              >
                Cancel
              </Button>
            </div>
          </form>
        </>
      )}
    </Formik>
  );
};

export default AddEditQuestion;
